<template>
  <div class="p-4 space-y-4">
    <!-- Task Name Fields -->
    <div>
      <label for="name-en" class="block text-sm font-medium text-gray-700">Name (EN)</label>
      <InputText id="name-en" v-model="form.nameEn" class="w-full" />
    </div>
    <div>
      <label for="name-de" class="block text-sm font-medium text-gray-700">Name (DE)</label>
      <InputText id="name-de" v-model="form.nameDe" class="w-full" />
    </div>

    <!-- Tags Field -->
    <div>
      <label for="tags" class="block text-sm font-medium text-gray-700"
        >Tags (comma separated)</label
      >
      <InputText id="tags" v-model="form.tagsInput" class="w-full" />
    </div>

    <!-- Enum Type Selection -->
    <div>
      <label for="enumType" class="block text-sm font-medium text-gray-700"
        >Select Task Type (Enum)</label
      >
      <select
        id="enumType"
        v-model="form.selectedEnumType"
        class="w-full p-2 border border-gray-300 rounded"
      >
        <option value="" disabled>Select an Enum Type</option>
        <option v-for="option in enumTypeOptions" :key="option.name" :value="option.name">
          {{ option.name }}
        </option>
      </select>
    </div>

    <!-- Enum Value Selection (only if an enum type is selected) -->
    <div v-if="form.selectedEnumType">
      <label for="enumValue" class="block text-sm font-medium text-gray-700"
        >Select Enum Value</label
      >
      <select
        id="enumValue"
        v-model="form.selectedEnumValue"
        class="w-full p-2 border border-gray-300 rounded"
      >
        <option value="" disabled>Select an Enum Value</option>
        <option
          v-for="value in enumValueOptions"
          :key="value.value"
          :value="value.value"
          :disabled="value.disabled"
        >
          {{ value.value }}{{ value.disabled ? ' (already used)' : '' }}
        </option>
      </select>
      <p class="text-xs text-gray-500 mt-1">
        Each assessment type can be used by only one schema (results are grouped by type).
      </p>
    </div>

    <!-- Question Rotation Settings -->
    <div class="border-t pt-4 mt-4">
      <h3 class="text-lg font-medium text-gray-900 mb-3">Question Rotation Settings</h3>

      <!-- Rotated Questions Schema Checkbox -->
      <div class="mb-4">
        <label class="flex items-center">
          <input
            type="checkbox"
            v-model="form.rotatedQuestionsSchema"
            class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <span class="text-sm font-medium text-gray-700">Enable Question Rotation</span>
        </label>
        <p class="text-xs text-gray-500 mt-1">
          When enabled, questions will be automatically rotated to prevent duplicates
        </p>
      </div>

      <!-- Rotation Period (only visible when rotation is enabled) -->
      <div v-if="form.rotatedQuestionsSchema" class="mb-4">
        <label for="rotationPeriod" class="block text-sm font-medium text-gray-700">
          Rotation Period
        </label>
        <select
          id="rotationPeriod"
          v-model="form.rotationPeriod"
          class="w-full p-2 border border-gray-300 rounded"
        >
      <!-- Note: 'hourly' removed - not supported in new structure
        (main cron runs hourly at :00) -->
      <option value="daily">Daily</option>
      <option value="weekly">Weekly</option>
      <option value="monthly">Monthly</option>
      <option value="yearly">Yearly</option>
        </select>
        <p class="text-xs text-gray-500 mt-1">
          How often the question pool resets
        </p>
      </div>

      <!-- Question Source -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">Question Source</label>
        <div class="space-y-2">
          <label class="flex items-center">
            <input
              type="radio"
              v-model="form.questionSource"
              value="direct"
              class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span class="text-sm text-gray-700">Direct from Schema</span>
          </label>
          <label class="flex items-center">
            <input
              type="radio"
              v-model="form.questionSource"
              value="dimensions"
              class="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
            />
            <span class="text-sm text-gray-700">From Dimensions</span>
          </label>
        </div>
        <p class="text-xs text-gray-500 mt-1">
          Where to source questions from when creating assessments
        </p>
      </div>

      <!-- Total Questions -->
      <div class="mb-4">
        <label for="totalQuestions" class="block text-sm font-medium text-gray-700">
          Total Questions per Assessment
        </label>
        <input
          type="number"
          id="totalQuestions"
          v-model.number="form.totalQuestions"
          min="1"
          max="50"
          class="w-full p-2 border border-gray-300 rounded"
        />
        <p class="text-xs text-gray-500 mt-1">
          Number of questions to include in each assessment (1-50)
        </p>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end space-x-2">
      <Button label="Cancel" class="p-button-secondary" @click="onCancel" />
      <Button
        label="Create Task"
        :disabled="!selectedType || usedSchemaTypes.has(selectedType)"
        @click="submit"
      />
    </div>
  </div>
</template>

<script>
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import { mapState } from 'vuex';
import AssessmentHelper from '@/helpers/AssessmentHelper';

export default {
  name: 'NewSchemaDialog',
  components: { InputText, Button },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {
        nameEn: '',
        nameDe: '',
        tagsInput: '',
        selectedEnumType: '',
        selectedEnumValue: '',
        rotatedQuestionsSchema: false,
        rotationPeriod: 'daily', // Changed from 'hourly' - hourly not supported in new structure
        questionSource: 'direct',
        totalQuestions: 10,
        // Schedule settings for Question Rotation
        questionRotationSchedule: {
          hour: 8,
          dayOfWeek: 1, // Monday
          dayOfMonth: 1,
        },
        // DevTips settings (new)
        devTipsEnabled: false,
        devTipsPeriod: 'weekly',
        devTipsSchedule: {
          hour: 8,
          dayOfWeek: 1,
          dayOfMonth: 1,
          month: 0,
        },
        // Level System settings (new)
        levelSystemEnabled: false,
        xpPerCompletion: 0,
        // First Login Task settings (new)
        firstLoginTaskEnabled: false,
      },
    };
  },
  computed: {
    ...mapState('enums', ['enumTypes']),
    ...mapState('assessmentSchemas', ['assessmentSchemas']),
    // Enum type options directly from the enums store.
    enumTypeOptions() {
      return this.enumTypes;
    },
    // Get enum values based on the selected enum type.
    enumValueOptions() {
      const selected = this.enumTypes.find((e) => e.name === this.form.selectedEnumType);
      const values = selected ? selected.values : [];
      return values.map((value) => ({
        ...value,
        disabled: this.usedSchemaTypes.has(value.value),
      }));
    },
    usedSchemaTypes() {
      return AssessmentHelper.usedSchemaTypes(this.assessmentSchemas);
    },
    selectedType() {
      const raw = this.form.selectedEnumValue;
      if (!raw) return '';
      return typeof raw === 'string' ? raw : raw.value;
    },
  },
  watch: {
    visible(isVisible) {
      if (!isVisible) {
        this.resetForm();
      }
    },
  },
  methods: {
    onHide() {
      this.$emit('update:visible', false);
      this.resetForm();
    },
    onCancel() {
      this.onHide();
    },
    resetForm() {
      this.form.nameEn = '';
      this.form.nameDe = '';
      this.form.tagsInput = '';
      this.form.selectedEnumType = '';
      this.form.selectedEnumValue = '';
      this.form.rotatedQuestionsSchema = false;
      this.form.rotationPeriod = 'daily';
      this.form.questionSource = 'direct';
      this.form.totalQuestions = 10;
      this.form.questionRotationSchedule = {
        hour: 8,
        dayOfWeek: 1,
        dayOfMonth: 1,
      };
      this.form.devTipsEnabled = false;
      this.form.devTipsPeriod = 'weekly';
      this.form.devTipsSchedule = {
        hour: 8,
        dayOfWeek: 1,
        dayOfMonth: 1,
        month: 0,
      };
      this.form.levelSystemEnabled = false;
      this.form.xpPerCompletion = 0;
      this.form.firstLoginTaskEnabled = false;
    },
    submit() {
      const selectedType = this.selectedType;
      if (!selectedType || this.usedSchemaTypes.has(selectedType)) {
        return;
      }
      // Parse tags from comma-separated input.
      const addedTags = this.form.tagsInput
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag);
      // Construct the schema object.
      const schemaData = {
        representationProperties: {
          assessmentName: { en: this.form.nameEn, de: this.form.nameDe },
        },
        tags: addedTags,
        type: selectedType,
        properties: {
          expirationTime: 1209600000,
          reminderTime: 172800000,
          linkAfterAssessment: '/my-results-dashboard',
          multiplierList: [],
        },
        // Use new config structure for all settings
        config: {
          // DevTips Configuration
          devTips: this.form.devTipsEnabled ? {
            enabled: true,
            schedule: {
              period: this.form.devTipsPeriod,
              hour: this.form.devTipsSchedule.hour,
              dayOfWeek: this.form.devTipsSchedule.dayOfWeek,
              dayOfMonth: this.form.devTipsSchedule.dayOfMonth,
              ...(this.form.devTipsPeriod === 'yearly' && {
                month: this.form.devTipsSchedule.month ?? 0,
              }),
            },
          } : undefined,
          // Question Rotation Configuration
          questionRotation: this.form.rotatedQuestionsSchema ? {
            enabled: true,
            schedule: {
              period: this.form.rotationPeriod,
              hour: this.form.questionRotationSchedule.hour,
              dayOfWeek: this.form.questionRotationSchedule.dayOfWeek,
              dayOfMonth: this.form.questionRotationSchedule.dayOfMonth,
            },
            questionSource: this.form.questionSource,
            totalQuestions: this.form.totalQuestions,
          } : undefined,
          // Level System Configuration
          levelSystem: this.form.levelSystemEnabled ? {
            enabled: true,
            xpPerCompletion: this.form.xpPerCompletion,
          } : undefined,
          // First Login Task Configuration
          firstLoginTask: this.form.firstLoginTaskEnabled ? {
            enabled: true,
          } : undefined,
        },
      };
      // Parent closes the dialog only after create succeeds (keep open on 409).
      this.$emit('create-schema', schemaData);
    },
  },
};
</script>

<style scoped>
/* Add any component-specific styles if needed */
</style>
